import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchHospitalitySnapshot } from '../../hospitality/infra/hospitalityRepo';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { buildAuthUserLabelById } from '../../auth/domain/authUserDisplayLabels';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useEmployeesStore, withSelfOwner } from '../../team/store/useEmployeesStore';
import { getPaymentMethods } from '../infra/salesRepo';
import {
  buildPaymentMethodNames,
  DEFAULT_PAYMENT_METHOD_LABELS,
} from '../infra/salesPaymentLabels';
import { loadSaleDetail } from './loadSaleDetail';
import { executeVoidSaleCommand } from './voidSaleCommand';
import { resolveVoidSaleBranchId } from './resolveVoidSaleBranchId';
import type { SaleDetail } from '../domain/salesTypes';
import { isPendingMercadoPagoQrSale } from '../../mercadopago/domain/isPendingMercadoPagoQrSale';
import type { CustomerVoidPlan } from '../../customers/domain-api';
import { buildCustomerVoidPlanFromSaleDetail } from '../../customers/domain-api';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { printSaleReceiptFromDetail } from '../../printing/application/printSaleTicket';
import { ticketPrintSuccessToast } from '../../printing/domain/printUserMessages';
import { fetchSalePickupTrackingUrl } from '../../printing/infra/pickupTrackingRepo';
import { shouldIncludePickupTrackingOnReceipt } from '../../printing/application/pickupTrackingReceiptPolicy';
import {
  authorizeSupervisorPin,
  branchHasSupervisorPin,
} from '../../shell/session/supervisorPinApi';
import { supabase } from '../../shared/supabase/supabaseClient';
import { FISCAL_SYSTEM_COPY, isFiscalInvoiceAwaitingCae } from '../../fiscal/domain-api';

export function useSaleDetail(params: {
  saleId: string;
  branchId: string | null | undefined;
  mercadopagoQrMethodAllowed?: boolean;
  onVoided?: () => void;
  onDetailMutated?: () => void;
  onBranchMismatch?: () => void;
}) {
  const {
    saleId,
    branchId,
    mercadopagoQrMethodAllowed = true,
    onVoided,
    onDetailMutated,
    onBranchMismatch,
  } = params;
  const { canVoid } = useBranchPermission('POS');
  const canReprint = useModulePermission('POS', 'reprint');
  const kitchenTicketsEnabled = useHasBusinessCapability('kitchen_tickets');
  const [hasSupervisorPin, setHasSupervisorPin] = useState(false);
  const sessionUser = useAuthStore((s) => s.session?.user ?? null);
  const sessionUserId = sessionUser?.id ?? null;
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);

  useEffect(() => {
    if (!saleId.trim()) return;
    void loadEmployees();
  }, [loadEmployees, saleId]);

  useEffect(() => {
    let alive = true;
    if (!branchId) {
      setHasSupervisorPin(false);
      return;
    }
    void branchHasSupervisorPin(branchId)
      .then((ok) => {
        if (alive) setHasSupervisorPin(ok);
      })
      .catch(() => {
        if (alive) setHasSupervisorPin(false);
      });
    return () => {
      alive = false;
    };
  }, [branchId]);
  const authUserLabels = useMemo(
    () =>
      buildAuthUserLabelById({
        sessionUserId: sessionUser?.id,
        sessionUserEmail: sessionUser?.email,
        ownerAuthUserId: sessionUser?.id,
        employees,
      }),
    [employees, sessionUser?.email, sessionUser?.id]
  );

  const [detail, setDetail] = useState<SaleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [voidLoading, setVoidLoading] = useState(false);
  const [voidConfirmVisible, setVoidConfirmVisible] = useState(false);
  const [voidSucceeded, setVoidSucceeded] = useState(false);
  const [customerVoidPlan, setCustomerVoidPlan] = useState<CustomerVoidPlan | null>(null);
  const [customerPreviewLoading, setCustomerPreviewLoading] = useState(false);
  const [paymentMethodNames, setPaymentMethodNames] = useState<Record<string, string>>({});
  const [paymentMethodNamesLoaded, setPaymentMethodNamesLoaded] = useState(false);
  const [diningTableName, setDiningTableName] = useState<string | null>(null);
  const [mpQrModalVisible, setMpQrModalVisible] = useState(false);
  const [reprintLoading, setReprintLoading] = useState(false);
  const mpQrModalVisibleRef = useRef(false);
  mpQrModalVisibleRef.current = mpQrModalVisible;

  const refresh = useCallback(async () => {
    if (!saleId.trim()) {
      setDetail(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const d = await loadSaleDetail(saleId, {
        authUserLabels,
        ownerAuthUserId: sessionUserId,
        skipMpQrSync: mpQrModalVisibleRef.current,
      });
      if (d && !d.customerOrderId) {
        try {
          const { data: linked } = await supabase
            .from('customer_orders')
            .select('id')
            .eq('sale_id', saleId)
            .maybeSingle();
          if (linked?.id) {
            setDetail({
              ...d,
              customerOrderId: String(linked.id),
              channel: d.channel ?? 'online',
            });
            return;
          }
        } catch {
          /* ignore link lookup */
        }
      }
      setDetail(d);
    } finally {
      setLoading(false);
    }
  }, [authUserLabels, saleId, sessionUserId]);

  const onMpQrTerminal = useCallback(() => {
    void refresh().finally(() => {
      onDetailMutated?.();
    });
  }, [onDetailMutated, refresh]);

  const closeMpQrModal = useCallback(() => {
    setMpQrModalVisible(false);
    void refresh().finally(() => {
      onDetailMutated?.();
    });
  }, [onDetailMutated, refresh]);

  useEffect(() => {
    if (!branchId || !detail?.branchId) return;
    if (detail.branchId !== branchId) {
      setDetail(null);
      setLoading(true);
      setVoidConfirmVisible(false);
      setMpQrModalVisible(false);
      onBranchMismatch?.();
    }
  }, [branchId, detail?.branchId, onBranchMismatch]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const prevInvoiceCaeRef = useRef<string | null>(null);
  const fiscalDetailInitRef = useRef(false);

  useEffect(() => {
    fiscalDetailInitRef.current = false;
    prevInvoiceCaeRef.current = null;
  }, [saleId]);

  useEffect(() => {
    if (!detail || !isFiscalInvoiceAwaitingCae(detail)) return;
    const interval = setInterval(() => {
      void refresh();
    }, 20_000);
    return () => clearInterval(interval);
  }, [
    detail?.id,
    detail?.fiscalMode,
    detail?.invoiceCae,
    detail?.invoiceStatus,
    detail?.fiscalStatus,
    detail?.invoiceLastError,
    refresh,
  ]);

  useEffect(() => {
    if (!detail) return;
    const cae = detail.invoiceCae?.trim() || null;
    if (!fiscalDetailInitRef.current) {
      fiscalDetailInitRef.current = true;
      prevInvoiceCaeRef.current = cae;
      return;
    }
    const prev = prevInvoiceCaeRef.current;
    if (cae && !prev && detail.fiscalMode === 'fiscal') {
      showSuccess(
        FISCAL_SYSTEM_COPY.detail.caeAuthorizedTitle,
        FISCAL_SYSTEM_COPY.detail.caeAuthorizedBody
      );
      onDetailMutated?.();
    }
    prevInvoiceCaeRef.current = cae;
  }, [detail, onDetailMutated]);

  useEffect(() => {
    setPaymentMethodNamesLoaded(false);
    getPaymentMethods()
      .then((m) => {
        const filtered = m.filter((row) => row.id !== 'mercadopago_qr' || mercadopagoQrMethodAllowed);
        setPaymentMethodNames({ ...DEFAULT_PAYMENT_METHOD_LABELS, ...buildPaymentMethodNames(filtered) });
        setPaymentMethodNamesLoaded(true);
      })
      .catch(() => {
        setPaymentMethodNames(DEFAULT_PAYMENT_METHOD_LABELS);
        setPaymentMethodNamesLoaded(true);
      });
  }, [mercadopagoQrMethodAllowed]);

  useEffect(() => {
    let alive = true;
    if (!branchId || !detail?.diningTableId) {
      setDiningTableName(null);
      return;
    }
    void fetchHospitalitySnapshot(branchId)
      .then(({ diningTables }) => {
        if (!alive) return;
        const table = diningTables.find((t) => t.id === detail.diningTableId);
        setDiningTableName(table?.name ?? null);
      })
      .catch(() => {
        if (!alive) return;
        setDiningTableName(null);
      });
    return () => {
      alive = false;
    };
  }, [branchId, detail?.diningTableId]);

  const onVoidPress = useCallback(() => {
    setVoidConfirmVisible(true);
    const targetSaleId = detail?.id ?? saleId;
    if (!targetSaleId.trim()) {
      setCustomerVoidPlan(null);
      return;
    }
    if (detail) {
      setCustomerVoidPlan(
        buildCustomerVoidPlanFromSaleDetail({
          id: detail.id,
          fiadoCustomerId: detail.fiadoCustomerId,
          customerAllocationPaid: detail.customerAllocationPaid,
          payments: detail.payments,
        })
      );
    } else {
      setCustomerVoidPlan(null);
    }
  }, [detail, saleId]);

  const onVoidConfirmClose = useCallback(() => {
    if (voidLoading) return;
    setVoidConfirmVisible(false);
    setVoidSucceeded(false);
  }, [voidLoading]);

  const onVoidConfirm = useCallback(
    async (reason: string, supervisorPin?: string) => {
      if (!detail?.id) {
        showError('No se pudo anular', 'No cargó el detalle. Refrescá e intentá de nuevo.');
        return;
      }

      const branchResolution = resolveVoidSaleBranchId({
        activeBranchId: branchId,
        saleBranchId: detail.branchId,
      });
      if (!branchResolution.ok) {
        showError('No se pudo anular', branchResolution.message);
        return;
      }

      setVoidLoading(true);
      try {
        let supervisorOverrideId: string | undefined;
        if (!canVoid) {
          if (!supervisorPin?.trim()) {
            showError('PIN requerido', 'Para anular hace falta el PIN de supervisor.');
            return;
          }
          supervisorOverrideId = await authorizeSupervisorPin({
            branchId: branchResolution.branchId,
            pin: supervisorPin.trim(),
            module: 'POS',
            action: 'void',
            context: { saleId: detail.id },
          });
        }

        await executeVoidSaleCommand({
          saleId: detail.id,
          branchId: branchResolution.branchId,
          voidReason: reason,
          voidedByUserId: sessionUserId,
          fiscalMode: detail.fiscalMode,
          supervisorOverrideId,
        });
        // Estado de éxito dentro del modal (proto ventas-ux-v1): resume qué
        // pasó con stock, cobros, NC y cuenta — no solo cerrar y cambiar.
        setVoidSucceeded(true);
        await refresh();
        onVoided?.();

        showSuccess(
          'Venta anulada',
          detail.fiscalMode === 'fiscal' && detail.invoiceCae?.trim()
            ? 'Quedó anulada. Stock y cuenta se revirtieron. Si no lo ves, refrescá Inventario. Si era factura, va nota de crédito.'
            : 'Quedó anulada. Stock y cuenta se revirtieron. Si no lo ves, refrescá Inventario.'
        );
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'No se pudo anular la venta.';
        showError('No se pudo anular', message);
      } finally {
        setVoidLoading(false);
      }
    },
    [detail, branchId, sessionUserId, refresh, onVoided, canVoid]
  );

  const canShowVoidAction =
    !!detail && detail.status !== 'voided' && (canVoid || hasSupervisorPin);
  const requireSupervisorPinForVoid = !canVoid && hasSupervisorPin;
  const canCollectMpQr =
    mercadopagoQrMethodAllowed && isPendingMercadoPagoQrSale(detail);

  const openMpQrModal = useCallback(() => setMpQrModalVisible(true), []);

  const onReprintPress = useCallback(async () => {
    if (!detail || reprintLoading) return;
    setReprintLoading(true);
    try {
      let trackingUrl: string | null = null;
      if (
        shouldIncludePickupTrackingOnReceipt({
          diningTableId: detail.diningTableId,
          diningTableName,
        })
      ) {
        try {
          trackingUrl = await fetchSalePickupTrackingUrl(detail.id);
        } catch {
          trackingUrl = null;
        }
      }
      const result = await printSaleReceiptFromDetail({
        detail,
        paymentMethodNames,
        diningTableName,
        kitchenTickets: kitchenTicketsEnabled,
        trackingUrl,
      });
      if (result.ok) {
        const copy = ticketPrintSuccessToast(result.transport);
        showSuccess(copy.title, copy.detail);
      }
    } finally {
      setReprintLoading(false);
    }
  }, [detail, diningTableName, kitchenTicketsEnabled, paymentMethodNames, reprintLoading]);

  return {
    detail,
    loading,
    diningTableName,
    paymentMethodNames,
    paymentMethodNamesLoaded,
    canVoid: canShowVoidAction,
    requireSupervisorPinForVoid,
    canReprint,
    reprintLoading,
    onReprintPress,
    canCollectMpQr,
    mpQrModalVisible,
    openMpQrModal,
    closeMpQrModal,
    onMpQrTerminal,
    voidLoading,
    voidConfirmVisible,
    voidSucceeded,
    customerVoidPlan,
    customerPreviewLoading,
    onVoidPress,
    onVoidConfirm,
    onVoidConfirmClose,
    refresh,
  };
}
